import { makeAutoObservable } from "mobx";
import httpClient from "@/config/httpClient";
import { fetchMe } from "@/utils/auth/me";
import { clearBugsnagUser, setBugsnagUser } from "@/utils/bugsnag/bugsnagConsent";
import { getErrorCode, isAuthErrorCode } from "@/utils/common/api/apiErrorCodes";
import { endpoints } from "@/utils/common/requests/endpoints";
import { crewStore, uiStore } from "../rootStore";
import construct from "../utils/construct";
import persist from "../utils/persist";

// eslint-disable-next-line
let _rootStore;

/**
 * @typedef {Object} UserStoreProps
 * @property {boolean} isAuthenticated Whether the user is authenticated (session cookie present and valid)
 * @property {{ username?: string, email?: string, [key: string]: any } | null} data The user data fetched from the server
 * @property {"tasks" | "items" | "money" | "split" | null} lastApp The last application the user interacted with
 * @property {{
 *  opt_thumbBurger: "hidden" | "left" | "right" | null,
 *  opt_groupShortcut: string | null,
 *  opt_tasksDashboardCrewScope: "current" | "all" | null,
 *  opt_dormantTaskCompletion: "always" | "whenActivated" | null,
 *  opt_language: "es" | "en" | null
 * }} settings
 */

/** @type {UserStoreProps} */
const defaultProperties = {
	isAuthenticated: false,
	data: null,
	lastApp: null,
	settings: {
		opt_thumbBurger: null,
		opt_groupShortcut: null,
		opt_tasksDashboardCrewScope: "all",
		opt_dormantTaskCompletion: "whenActivated",
		// null = sin preferencia guardada: manda el idioma del navegador (detectBrowserLanguage)
		opt_language: null,
	},
};

export default class UserStore {
	isAuthenticated = defaultProperties.isAuthenticated;
	data = defaultProperties.data;
	lastApp = defaultProperties.lastApp;
	settings = defaultProperties.settings;

	constructor(rootStore) {
		construct(this, defaultProperties);
		_rootStore = rootStore;

		makeAutoObservable(this);
		persist(this, "userStore", { excludeKey: ["isAuthenticated"] });
	}

	/**
	 * @param {keyof typeof defaultProperties["settings"]} key
	 */
	getSetting(key) {
		return this.settings[key];
	}

	/**
	 * @param {keyof typeof defaultProperties["settings"]} key
	 * @returns
	 */
	setSetting(key, value) {
		// @ts-expect-error settings dynamic assignment
		this.settings[key] = value;
	}

	reset() {
		construct(this, defaultProperties);
		clearBugsnagUser();
	}

	async fetchMe() {
		const { success, me, status } = await fetchMe();

		if (!success) {
			// status 0 (network/CORS-blocked) or 401/403 → treat as not authenticated,
			// since the most common case is a fresh session with no cookie.
			// Auth 401 bodies are raw {errorCode: "JWT_MISSING"|"JWT_INVALID"} (no errorId),
			// and legacy bodies used {errorCode: "TOKEN_INVALID"}.
			const isAuthError =
				status === 0
				|| status === 401
				|| status === 403
				|| isAuthErrorCode(getErrorCode(me));
			return { error: isAuthError ? "auth" : "server" };
		}

		setBugsnagUser(me?.id, me?.email, me?.username);

		this.data = me;

		return { me };
	}

	async fetchSettings() {
		const { response, isError } = await httpClient.get(endpoints.settings.GET());

		if (isError) {
			return console.log("[UserStore.fetchSettings] error");
		}

		this.settings = {
			opt_dormantTaskCompletion: "whenActivated",
			// null si el backend no lo manda: se respeta la detección del navegador
			opt_language: null,
			...response,
		};
	}

	async init() {
		console.log("[agora] INIT: Iniciando...");

		const meResult = await this.fetchMe();

		if (meResult?.error) {
			console.log("[agora] INIT: error", meResult.error);
			this.isAuthenticated = false;
			return { error: meResult.error };
		}

		// Only fetch protected data once we know the user is authenticated.
		await Promise.allSettled([this.fetchSettings(), crewStore.fetchMyCrews(true)]);

		// Aplica el idioma guardado en settings (sin re-persistir en backend)
		const serverLanguage = this.settings?.opt_language;
		if (serverLanguage && serverLanguage !== uiStore.language) {
			uiStore.setLanguage(serverLanguage, { persist: false });
		}

		this.isAuthenticated = true;
		console.log("[agora] INIT: ok");
		return { ok: true };
	}
}
